import React, { useState, useEffect } from 'react';
import axiosInstance from '../../../axiosInstance';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../components/Sidebar';
import Navbar from '../../components/Navbar';
import toast from 'react-hot-toast';


const CreateOrder = () => {

  const navigate = useNavigate();

  const [AllProduct, setAllProduct] = useState([]);
  const [loading, setLoading] = useState(true);
  const [zones, setZones] = useState([]);
  const [filteredCities, setFilteredCities] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [Product, setProduct] = useState({});
  const [ProductPrice, setProductPrice] = useState({ regular: 0, sale: 0 });
  const [isNewUser, setIsNewUser] = useState(false);

  const [formData, setFormData] = useState({
    productList: "",
    service: "",
    category: "",
    fullname: "",
    phone: "",
    email: "",
    address: "",
    pincode: "",
    state: "",
    statename: "",
    city: "",
    bookingDate: "",
    bookingTime: "",
    requirement: "",
    totalAmount: 0,
    order: 2
  });

  // ─── Fetch products + zones on mount ─────────────────────────────────────
  useEffect(() => {
    const fetchProductData = async () => {
      try {
        const res = await axiosInstance.get("/all-products");
        setAllProduct(res.data?.products || []);
      } catch (err) {
        console.error("Error fetching products:", err);
      }
    };

    const fetchZones = async () => {
      try {
        const res = await axiosInstance.get("/get-all-zones");
        setZones(res.data.Zones || []);
      } catch (err) {
        console.error("Error fetching zones:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchProductData();
    fetchZones();
  }, []);

  // ─── Load product details + price on select ───────────────────────────────
  const getProduct = async (slug) => {
    if (!slug) return;
    try {
      const { data } = await axiosInstance.get(`/user-product-slug/${slug}`);
      const p = data.Product;
      setProduct(p);
      const sale = p?.salePrice || 0;
      const regular = p?.regularPrice || 0;
      setProductPrice({ regular, sale });
      setFormData((prev) => ({
        ...prev,
        service: p?.title || "",
        category: p?.Category || "",
        totalAmount: sale > 0 ? sale : regular,
        image: p?.image
      }));
    } catch (err) {
      console.error("Error getting product:", err);
    }
  };

  // ─── Auto-fill user by phone ──────────────────────────────────────────────
  const fetchUserByPhone = async (phone) => {
    try {
      const res = await axiosInstance.get(`/get-user-by-phone/${phone}`);

      if (res.data?.newUser) {
        setIsNewUser(true);
        toast("New user — a new account will be created on submit.", { icon: "ℹ️" });
        return;
      }

      setIsNewUser(false);
      const user = res.data?.user;
      if (!user) return;

      toast.success("User found! Details auto-filled.");

      // Match state _id from zones
      let stateId = "";
      let statename = "";
      if (user.state) {
        const matched = zones.find(
          (s) => s.name?.toLowerCase() === user.state?.toLowerCase() || s._id === user.state
        );
        if (matched) {
          stateId = matched._id;
          statename = matched.name;
          setFilteredCities(matched.cities || []);
        }
      }

      setFormData((prev) => ({
        ...prev,
        fullname: user.username || "",
        email: user.email || "",
        address: user.address || "",
        pincode: user.pincode || "",
        state: stateId || user.state || "",
        statename: statename || user.statename || "",
        city: user.city || "",
      }));
    } catch (err) {
      console.error("fetchUserByPhone error:", err);
    }
  };

  // ─── Helpers ──────────────────────────────────────────────────────────────
  const capitalizeEachWord = (str) => {
    if (!str) return str;
    return str
      .replace(/\d+/g, "")
      .split(" ")
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
      .join(" ");
  };

  const validateEmail = (email) => {
    const invalid = /[^\w@_.]| /;
    if (email === "-" || email === "+") return "";
    return invalid.test(email) ? "" : email;
  };

  // ─── Change handler ───────────────────────────────────────────────────────
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    let val = type === "checkbox" ? checked : value;

    if (name === "fullname") val = capitalizeEachWord(val);
    if (name === "email") val = validateEmail(val);

    if (name === "phone") {
      val = value.replace(/\D/g, "").slice(0, 13);
      if (val.length === 10) fetchUserByPhone(val);
    }

    if (name === "productList") getProduct(val);

    if (name === "state") {
      const sel = zones.find((s) => s._id === val);
      if (sel) {
        setFilteredCities(sel.cities || []);
        setFormData((prev) => ({ ...prev, statename: sel.name, city: "" }));
      }
    }

    setFormData((prev) => ({ ...prev, [name]: val }));
  };

  // ─── Submit ───────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    const required = [
      { name: "productList", message: "Please select a Product" },
      { name: "fullname", message: "Please enter Full Name" },
      { name: "phone", message: "Please enter Phone" },
      { name: "email", message: "Please enter Email" },
      { name: "address", message: "Please enter Address" },
      { name: "pincode", message: "Please enter Pincode" },
      { name: "state", message: "Please select State" },
      { name: "city", message: "Please select City" },

    ];

    for (const f of required) {
      if (!formData[f.name]) { toast.error(f.message); return; }
    }

    try {
      setSubmitting(true);
      await toast.promise(
        axiosInstance.post("/admin/create-order", { ...formData, isNewUser }),
        {
          loading: "Creating order...",
          success: "Order created successfully!",
          error: "Failed to create order.",
        }
      );
      navigate("/all-order");
    } catch (err) {
      console.error("Submit error:", err);
    } finally {
      setSubmitting(false);
    }
  };


  return (
    <>
      <Sidebar />
      <main className="content">
        <Navbar />

        <div id="main-content" className="bg-transparent">
          <div className="p-3 rounded bg-white mt-4">

            <h5 className="mb-4 fw-semibold">Create New Order</h5>

            <div className="row">

              {/* ── Product ── */}
              <div className="mb-4 col-md-6">
                <label className="form-label text-dark" htmlFor="productList">
                  Product <span className="text-danger">(Required)</span>
                </label>
                <select
                  className="form-control"
                  id="productList"
                  name="productList"
                  value={formData.productList}
                  onChange={handleChange}
                >
                  <option value="">Select Product</option>
                  {AllProduct.map((item) => (
                    <option key={item._id} value={item.slug}>{item.title}</option>
                  ))}
                </select>
              </div>

              {/* ── Price Card ── */}
              <div className="col-md-6 mb-4 d-flex align-items-end">
                {Product?.title ? (
                  <div
                    className="w-100 rounded px-3 py-2"
                    style={{ background: "#f0f7ff", border: "1px solid #c8e0ff" }}
                  >
                    <div className="fw-semibold text-truncate" style={{ fontSize: "13px", color: "#333" }}>
                      {Product.title}
                    </div>
                    <div className="d-flex align-items-center gap-2 mt-1">
                      {ProductPrice.sale > 0 ? (
                        <>
                          <span className="fw-bold" style={{ fontSize: "16px", color: "#0d6efd" }}>
                            ₹{Number(ProductPrice.sale).toLocaleString("en-IN")}
                          </span>
                          <span className="text-decoration-line-through text-muted" style={{ fontSize: "13px" }}>
                            ₹{Number(ProductPrice.regular).toLocaleString("en-IN")}
                          </span>
                          <span className="badge" style={{ background: "#e6f4ea", color: "#1a7f37", fontSize: "11px", fontWeight: 600 }}>
                            {Math.round(((ProductPrice.regular - ProductPrice.sale) / ProductPrice.regular) * 100)}% OFF
                          </span>
                        </>
                      ) : (
                        <span className="fw-bold" style={{ fontSize: "16px", color: "#0d6efd" }}>
                          ₹{Number(ProductPrice.regular).toLocaleString("en-IN")}
                        </span>
                      )}
                    </div>
                  </div>
                ) : (
                  <div
                    className="w-100 rounded px-3 py-2 text-muted"
                    style={{ background: "#f8f9fa", border: "1px dashed #dee2e6", fontSize: "13px" }}
                  >
                    Select a product to see price
                  </div>
                )}
              </div>

              {/* ── Phone ── */}
              <div className="form-outline col-md-6 mb-4">
                <label className="form-label" htmlFor="phone">
                  Phone No. <span className="text-danger">(Required)</span>
                  <small className="ms-1" style={{ color: isNewUser ? "#e67e00" : "#888" }}>
                    {isNewUser ? "⚡ New user — will be registered" : "(auto-fills if user exists)"}
                  </small>
                </label>
                <input
                  type="number"
                  id="phone"
                  name="phone"
                  className="form-control"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="Enter 10-digit phone"
                  style={{ boxShadow: "none" }}
                />
              </div>

              {/* ── Full Name ── */}
              <div className="form-outline col-md-6 mb-4">
                <label className="form-label" htmlFor="fullname">
                  Full Name <span className="text-danger">(Required)</span>
                </label>
                <input
                  type="text" id="fullname" name="fullname"
                  className="form-control"
                  value={formData.fullname}
                  onChange={handleChange}
                  style={{ boxShadow: "none" }}
                />
              </div>

              {/* ── Email ── */}
              <div className="form-outline col-md-6 mb-4">
                <label className="form-label" htmlFor="email">
                  Email <span className="text-danger">(Required)</span>
                </label>
                <input
                  type="email" id="email" name="email"
                  className="form-control"
                  value={formData.email}
                  onChange={handleChange}
                  style={{ boxShadow: "none" }}
                />
              </div>

              {/* ── Address ── */}
              <div className="form-outline col-md-12 mb-4">
                <label className="form-label" htmlFor="address">
                  Full Address <span className="text-danger">(Required)</span>
                </label>
                <textarea
                  id="address" name="address"
                  className="form-control"
                  value={formData.address}
                  onChange={handleChange}
                  style={{ boxShadow: "none" }}
                />
              </div>

              {/* ── Pincode ── */}
              <div className="form-outline col-md-6 mb-4">
                <label className="form-label" htmlFor="pincode">
                  Pincode <span className="text-danger">(Required)</span>
                </label>
                <input
                  type="text" id="pincode" name="pincode"
                  className="form-control"
                  value={formData.pincode}
                  onChange={handleChange}
                  style={{ boxShadow: "none" }}
                />
              </div>

              {/* ── State ── */}
              <div className="mb-4 col-md-6">
                <label className="form-label text-dark" htmlFor="state">
                  State <span className="text-danger">(Required)</span>
                </label>
                <select
                  className="form-control" id="state" name="state"
                  value={formData.state}
                  onChange={handleChange}
                >
                  <option value="" disabled>
                    {loading ? "Loading..." : "Select State"}
                  </option>
                  {zones.map((item) => (
                    <option key={item._id} value={item._id}>{item.name}</option>
                  ))}
                </select>
              </div>

              {/* ── City ── */}
              <div className="mb-4 col-md-6">
                <label className="form-label text-dark" htmlFor="city">
                  City <span className="text-danger">(Required)</span>
                </label>
                <select
                  className="form-control" id="city" name="city"
                  value={formData.city}
                  onChange={handleChange}
                >
                  <option value="">Select City</option>
                  {filteredCities.map((city, i) => (
                    <option key={i} value={city}>{city}</option>
                  ))}
                </select>
              </div>



            </div>

            {/* ── Submit ── */}
            <div className="d-flex justify-content-end mt-2 mb-2">
              <button
                type="button"
                className="btn btn-primary px-4"
                onClick={handleSubmit}
                disabled={submitting}
              >
                {submitting ? "Submitting..." : "Submit Order"}
              </button>
            </div>

          </div>
        </div>
      </main>
    </>
  );
};

export default CreateOrder;